import { Controller, useFormContext } from 'react-hook-form';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Lib
import {
    social_media_tool,
    social_profiles_managed,
    team_size
} from '@/lib/signup/userInfos/marketingOptions';

import FormFieldMessage from '@/components/base/FormFieldMessage';
import Input from '@/components/base/Input';
//Components
import Label from '@/components/base/Label';
import { Select } from '@/components/base/Select/SelectComponent';
import RadioButton from '@/components/composite/Radio/RadioButton';
import RadioImage from '@/components/composite/Radio/RadioImage';
// Types
import { UserInfosFormType } from '@/components/pages/signup/userInfos/UserInfosForm';

const SubStepTwoForm = () => {
    const { t } = useTranslation('signup_funnel');

    const {
        register,
        control,
        watch,
        formState: { errors }
    } = useFormContext<UserInfosFormType>();

    return (
        <>
            <div>
                <Label required fontType="section">
                    {t('user_infos.sub_step_2.social_profiles_managed.label')}
                </Label>
                <fieldset className="flex flex-wrap">
                    {social_profiles_managed(t).map((opt) => (
                        <RadioButton
                            key={opt.value}
                            id={`social_profiles_managed_${opt.value}`}
                            value={opt.value}
                            checked={watch('social_profiles_managed') === opt.value}
                            className="mb-1.5 mr-1.5"
                            data-testid={`social_profiles_managed_${opt.value}`}
                            {...register('social_profiles_managed', {
                                required: t(
                                    'user_infos.errors.error_social_profiles_managed_required'
                                )
                            })}>
                            {opt.label}
                        </RadioButton>
                    ))}
                </fieldset>
                {errors?.social_profiles_managed ? (
                    <FormFieldMessage className="mt-2">
                        {errors.social_profiles_managed.message}
                    </FormFieldMessage>
                ) : null}
            </div>
            <div>
                <Label required fontType="section">
                    {t('user_infos.sub_step_2.team_size.label')}
                </Label>
                <fieldset className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {team_size(t).map((opt) => (
                        <RadioImage
                            key={opt.value}
                            id={`team_size_${opt.value}`}
                            src={opt.src}
                            label={opt.label}
                            subLabel={opt.subLabel}
                            value={opt.value}
                            checked={watch('team_size') === opt.value}
                            data-testid={`team_size_${opt.value}`}
                            {...register('team_size', {
                                required: t('user_infos.errors.error_team_size_required')
                            })}
                        />
                    ))}
                </fieldset>
                {errors?.team_size ? (
                    <FormFieldMessage className="mt-2">{errors.team_size.message}</FormFieldMessage>
                ) : null}
            </div>
            <div>
                <Label required fontType="section">
                    {t('user_infos.sub_step_2.social_media_tool.label')}
                </Label>
                <div className="flex w-full flex-col space-y-m xl:flex-row xl:space-x-3 xl:space-y-0">
                    <Controller
                        control={control}
                        name="social_media_tool"
                        rules={{
                            validate: (el) =>
                                el !== null ||
                                t('user_infos.errors.error_social_media_tool_required')
                        }}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                className="max-w-full"
                                selected={
                                    social_media_tool(t).find((item) => item.value === value) ?? {
                                        label: t(
                                            'user_infos.sub_step_2.social_media_tool.placeholder'
                                        ),
                                        value: null
                                    }
                                }
                                panelOptions={{ placement: 'top' }}
                                buttonVariant="lightBorder"
                                setSelected={(el) => {
                                    onChange(el.value);
                                }}
                                required={true}
                                error={errors?.social_media_tool?.message}
                                options={social_media_tool(t)}
                            />
                        )}
                    />
                    <div className="w-full">
                        {watch('social_media_tool') === 'other' ? (
                            <Input
                                placeholder={t('user_infos.sub_step_2.social_media_tool.specify')}
                                type="text"
                                id="social_media_tool_detail"
                                name="social_media_tool_detail"
                                error={errors?.social_media_tool_detail?.message}
                                data-testid="social_media_tool_detail"
                                {...register('social_media_tool_detail')}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubStepTwoForm;
